import { useState, useEffect, useCallback } from "react";
import { adminFetch } from "./adminUtils";
import { toast } from "sonner";
import {
  Coins, Plus, Pencil, Trash2, Loader2, Check, X as XIcon,
  Users, ShieldCheck, Package, Zap, ArrowUpRight, CreditCard,
  Settings2, Save, Eye, EyeOff, BookOpen,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";

const ACTION_LABELS = {
  image_gen_dish: "Imagen plato",
  image_gen_ingredient: "Imagen ingrediente",
  image_gen_logo: "Logo",
  recipe_gen: "Receta",
  menu_gen: "Menu completo",
  forecast: "Prevision IA",
  invoice_extract: "Factura",
  menu_import: "Importar menu",
  brand_ambiance: "Identidad visual",
  website_content: "Contenido web",
  waste_estimate: "Estimar merma",
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [rules, setRules] = useState([]);
  const [topUpRequests, setTopUpRequests] = useState([]);
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [tab, setTab] = useState("plans");
  const [addCredits, setAddCredits] = useState({});
  const [pricingConfig, setPricingConfig] = useState(null);
  const [offers, setOffers] = useState([]);
  const [editingOffer, setEditingOffer] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [p, s, r, t, u, pc, of_] = await Promise.all([
        adminFetch("/api/admin/plans"),
        adminFetch("/api/admin/subscriptions"),
        adminFetch("/api/admin/credit-rules"),
        adminFetch("/api/admin/top-up-requests"),
        adminFetch("/api/admin/upgrade-requests"),
        adminFetch("/api/admin/pricing-config").catch(() => null),
        adminFetch("/api/admin/offers").catch(() => []),
      ]);
      setPlans(p);
      setSubs(s);
      setRules(r);
      setTopUpRequests(t.filter(x => x.status === "pending"));
      setUpgradeRequests(u.filter(x => x.status === "pending"));
      setPricingConfig(pc || { annual_discount_type: "none", annual_discount_percent: 0, annual_free_months: 0 });
      setOffers(of_ || []);
    } catch {
      toast.error("Error cargando datos");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const savePricingConfig = async (patch) => {
    try {
      const next = { ...(pricingConfig || {}), ...patch };
      const saved = await adminFetch("/api/admin/pricing-config", {
        method: "PUT",
        body: JSON.stringify(next),
      });
      setPricingConfig(saved);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error guardando configuración");
    }
  };

  const togglePlanLandingVisibility = async (plan) => {
    const next = !(plan.is_visible_on_landing !== false);  // default true → toggle to false
    try {
      await adminFetch(`/api/admin/plans/${plan.plan_id}`, {
        method: "PUT",
        body: JSON.stringify({ is_visible_on_landing: next }),
      });
      setPlans(prev => prev.map(p => p.plan_id === plan.plan_id ? { ...p, is_visible_on_landing: next } : p));
      toast.success(next ? "Plan visible en landing" : "Plan oculto de la landing");
    } catch {
      toast.error("Error al cambiar visibilidad");
    }
  };

  const savePlan = async (plan) => {
    try {
      if (plan.plan_id && !plan._isNew) {
        await adminFetch(`/api/admin/plans/${plan.plan_id}`, { method: "PUT", body: JSON.stringify(plan) });
      } else {
        await adminFetch("/api/admin/plans", { method: "POST", body: JSON.stringify(plan) });
      }
      toast.success("Plan guardado");
      setEditingPlan(null);
      loadData();
    } catch { toast.error("Error guardando plan"); }
  };

  const deletePlan = async (planId) => {
    if (!confirm("Desactivar este plan?")) return;
    try {
      await adminFetch(`/api/admin/plans/${planId}`, { method: "DELETE" });
      toast.success("Plan desactivado");
      loadData();
    } catch { toast.error("Error"); }
  };

  const saveRule = async (action, credits) => {
    try {
      await adminFetch(`/api/admin/credit-rules/${action}`, {
        method: "PUT", body: JSON.stringify({ credits: Number(credits) }),
      });
      toast.success("Regla actualizada");
      setEditingRule(null);
      loadData();
    } catch { toast.error("Error"); }
  };

  const handleAddCredits = async (subId) => {
    const amt = addCredits[subId];
    if (!amt || amt <= 0) return;
    try {
      await adminFetch(`/api/admin/subscriptions/${subId}/add-credits`, {
        method: "PUT", body: JSON.stringify({ credits: Number(amt) }),
      });
      toast.success(`${amt} creditos añadidos`);
      setAddCredits(prev => ({ ...prev, [subId]: "" }));
      loadData();
    } catch { toast.error("Error"); }
  };

  const handleTopUpRequest = async (reqId, action) => {
    try {
      await adminFetch(`/api/admin/top-up-requests/${reqId}`, {
        method: "PUT", body: JSON.stringify({ action }),
      });
      toast.success(action === "approve" ? "Aprobado" : "Rechazado");
      loadData();
    } catch { toast.error("Error"); }
  };

  const handleUpgradeRequest = async (reqId, action) => {
    try {
      await adminFetch(`/api/admin/upgrade-requests/${reqId}`, {
        method: "PUT", body: JSON.stringify({ action }),
      });
      toast.success(action === "approve" ? "Upgrade aprobado" : "Upgrade rechazado");
      loadData();
    } catch (e) { toast.error(e.message || "Error"); }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-plans-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-500/15 rounded-xl flex items-center justify-center">
            <Coins className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Planes y Creditos</h1>
            <p className="text-muted-foreground text-xs">Gestiona suscripciones, precios y reglas de consumo</p>
          </div>
        </div>
        {(topUpRequests.length + upgradeRequests.length) > 0 && (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
            {topUpRequests.length + upgradeRequests.length} solicitudes pendientes
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-900 p-1 rounded-xl">
        {[
          { id: "plans", label: "Planes", icon: CreditCard },
          { id: "subs", label: "Suscripciones", icon: Users },
          { id: "rules", label: "Reglas creditos", icon: Settings2 },
          { id: "requests", label: `Solicitudes ${topUpRequests.length > 0 ? `(${topUpRequests.length})` : ""}`, icon: Package },
          { id: "upgrades", label: `Upgrades ${upgradeRequests.length > 0 ? `(${upgradeRequests.length})` : ""}`, icon: ArrowUpRight },
          { id: "pricing", label: "Precios/Ofertas", icon: Zap },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-colors
              ${tab === t.id ? "bg-violet-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* PLANS TAB */}
      {tab === "plans" && (
        <div className="space-y-4">
          {/* Beta visibility master switch */}
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex items-center justify-between gap-4"
            data-testid="landing-visibility-master-card"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {(pricingConfig?.landing_plans_visible !== false)
                  ? <Eye className="w-4 h-4 text-emerald-400" />
                  : <EyeOff className="w-4 h-4 text-amber-400" />}
                <h3 className="text-sm font-semibold text-white">Mostrar planes en la landing</h3>
                {(pricingConfig?.landing_plans_visible === false) && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Modo Beta</Badge>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Switch maestro: cuando está apagado, la landing pública oculta TODOS los planes y muestra el mensaje de Beta privada. Cada plan tiene además su propio toggle individual abajo.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pricingConfig?.landing_plans_visible !== false}
              onClick={() => savePricingConfig({
                landing_plans_visible: !(pricingConfig?.landing_plans_visible !== false)
              })}
              data-testid="landing-visibility-master-toggle"
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                pricingConfig?.landing_plans_visible !== false ? "bg-emerald-500" : "bg-slate-700"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                pricingConfig?.landing_plans_visible !== false ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
          </div>

          {/* Beta message (only relevant when master is OFF, but always editable) */}
          {(pricingConfig?.landing_plans_visible === false) && (
            <div className="bg-slate-900/50 border border-amber-500/20 rounded-xl p-4">
              <Label className="text-xs text-amber-300 mb-1 block">Mensaje mostrado en la landing</Label>
              <Input
                value={pricingConfig?.landing_beta_message || ""}
                placeholder="Estamos en Beta privada. Contáctanos para acceso anticipado."
                onChange={(e) => setPricingConfig(p => ({ ...p, landing_beta_message: e.target.value }))}
                onBlur={(e) => savePricingConfig({ landing_beta_message: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white text-sm"
                data-testid="landing-beta-message-input"
                maxLength={280}
              />
              <p className="text-[10px] text-muted-foreground mt-1">Máx. 280 caracteres. Se guarda al perder el foco.</p>
            </div>
          )}

          {/* User guide visibility toggle (in-app docs) */}
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex items-center justify-between gap-4"
            data-testid="user-guide-visibility-card"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className={`w-4 h-4 ${pricingConfig?.user_guide_visible ? "text-emerald-400" : "text-muted-foreground"}`} />
                <h3 className="text-sm font-semibold text-white">Guía de usuario en la app</h3>
                {!pricingConfig?.user_guide_visible && (
                  <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 text-[10px]">Oculta</Badge>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Cuando está apagado (por defecto en producción), se ocultan los enlaces a la guía PDF/HTML en
                <em> Configuración → Ayuda</em> y en el dropdown del usuario. Útil mientras la documentación está siendo revisada.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!pricingConfig?.user_guide_visible}
              onClick={() => savePricingConfig({
                user_guide_visible: !pricingConfig?.user_guide_visible
              })}
              data-testid="user-guide-visibility-toggle"
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                pricingConfig?.user_guide_visible ? "bg-emerald-500" : "bg-slate-700"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                pricingConfig?.user_guide_visible ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
          </div>

          <div className="flex justify-end">
            <Button
              size="sm"
              className="bg-violet-600 hover:bg-violet-500"
              onClick={() => setEditingPlan({ _isNew: true, name: "", price: 0, credits_monthly: 100, features: [], top_up_enabled: true, is_active: true, sort_order: plans.length })}
              data-testid="add-plan-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo plan
            </Button>
          </div>

          {/* Plan form */}
          {editingPlan && (
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-5 space-y-4">
              <h3 className="text-slate-200 font-semibold text-sm">{editingPlan._isNew ? "Nuevo plan" : "Editar plan"}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-slate-400">Nombre</Label>
                  <Input value={editingPlan.name} onChange={(e) => setEditingPlan(p => ({ ...p, name: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="plan-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Precio (EUR/mes + IVA)</Label>
                  <Input type="number" value={editingPlan.price} onChange={(e) => setEditingPlan(p => ({ ...p, price: Number(e.target.value) }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="plan-price-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Precio anual (EUR/ano + IVA)</Label>
                  <Input type="number" value={editingPlan.price_annual || 0} onChange={(e) => setEditingPlan(p => ({ ...p, price_annual: Number(e.target.value) }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="plan-price-annual-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Max restaurantes</Label>
                  <Input type="number" min={1} value={editingPlan.max_restaurants || 1} onChange={(e) => setEditingPlan(p => ({ ...p, max_restaurants: Number(e.target.value) }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="plan-max-rest-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Creditos/mes</Label>
                  <Input type="number" value={editingPlan.credits_monthly} onChange={(e) => setEditingPlan(p => ({ ...p, credits_monthly: Number(e.target.value) }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="plan-credits-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Orden</Label>
                  <Input type="number" value={editingPlan.sort_order} onChange={(e) => setEditingPlan(p => ({ ...p, sort_order: Number(e.target.value) }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Caracteristicas (una por linea)</Label>
                <textarea
                  rows={3}
                  value={(editingPlan.features || []).join("\n")}
                  onChange={(e) => setEditingPlan(p => ({ ...p, features: e.target.value.split("\n").filter(Boolean) }))}
                  className="w-full mt-1 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg p-2"
                  data-testid="plan-features-input"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={editingPlan.top_up_enabled}
                    onChange={(e) => setEditingPlan(p => ({ ...p, top_up_enabled: e.target.checked }))}
                    className="rounded" />
                  Permite recargas
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={editingPlan.is_active}
                    onChange={(e) => setEditingPlan(p => ({ ...p, is_active: e.target.checked }))}
                    className="rounded" />
                  Activo
                </label>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-violet-600 hover:bg-violet-500" onClick={() => savePlan(editingPlan)} data-testid="save-plan-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> Guardar
                </Button>
                <Button size="sm" variant="outline" className="border-slate-700 text-slate-300" onClick={() => setEditingPlan(null)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* Plans list */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((p) => (
              <div key={p.plan_id} className={`bg-slate-900 border rounded-xl p-5 ${p.is_active ? "border-slate-700" : "border-slate-800 opacity-50"}`} data-testid={`admin-plan-${p.plan_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-white font-semibold">{p.name}</h3>
                    {p.is_trial && <span className="text-[10px] text-amber-400">Trial {p.trial_days}d</span>}
                    {!p.is_trial && (
                      <button
                        type="button"
                        onClick={() => togglePlanLandingVisibility(p)}
                        data-testid={`plan-landing-visibility-${p.plan_id}`}
                        title={p.is_visible_on_landing !== false ? "Visible en landing — clic para ocultar" : "Oculto en landing — clic para mostrar"}
                        className={`mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${
                          p.is_visible_on_landing !== false
                            ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                            : "bg-amber-500/15 text-amber-400 hover:bg-amber-500/25"
                        }`}
                      >
                        {p.is_visible_on_landing !== false
                          ? <><Eye className="w-2.5 h-2.5" /> En landing</>
                          : <><EyeOff className="w-2.5 h-2.5" /> Oculto</>}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditingPlan({ ...p })} className="p-1.5 rounded-lg hover:bg-slate-800 text-muted-foreground hover:text-white" data-testid={`edit-plan-${p.plan_id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!p.is_trial && (
                      <button onClick={() => deletePlan(p.plan_id)} className="p-1.5 rounded-lg hover:bg-slate-800 text-muted-foreground hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{p.price}<span className="text-sm text-muted-foreground font-normal">/mes + IVA</span></p>
                {p.price_annual > 0 && (
                  <p className="text-xs text-muted-foreground">{p.price_annual}€/ano + IVA</p>
                )}
                {p.max_restaurants > 0 && (
                  <p className="text-xs text-muted-foreground">Hasta {p.max_restaurants} restaurante{p.max_restaurants > 1 ? "s" : ""}</p>
                )}
                <p className="text-xs text-violet-400 mt-1">{p.credits_monthly} creditos</p>
                <ul className="mt-3 space-y-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Check className="w-2.5 h-2.5 text-emerald-500" />{f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUBSCRIPTIONS TAB */}
      {tab === "subs" && (
        <div className="space-y-3">
          {subs.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-10">No hay suscripciones activas</p>
          ) : (
            subs.map((s) => {
              const pct = s.pct_used || 0;
              const barC = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
              return (
                <div key={s.subscription_id} className="bg-slate-900 border border-slate-700 rounded-xl p-4" data-testid={`sub-${s.subscription_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-white text-sm font-medium">{s.restaurant_name}</span>
                      <Badge className={`ml-2 text-[10px] ${s.status === "active" ? "bg-emerald-500/15 text-emerald-400" : s.status === "trial" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>
                        {s.status}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">{s.plan_name}</span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${barC}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <span className="text-xs text-slate-400 w-28 text-right">{s.credits_remaining}/{s.credits_total} cr</span>
                  </div>
                  {/* Add credits */}
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      placeholder="Creditos"
                      value={addCredits[s.subscription_id] || ""}
                      onChange={(e) => setAddCredits(prev => ({ ...prev, [s.subscription_id]: e.target.value }))}
                      className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-xs"
                    />
                    <Button size="sm" variant="outline" className="h-8 border-slate-700 text-slate-300 text-xs"
                      onClick={() => handleAddCredits(s.subscription_id)} data-testid={`add-credits-${s.subscription_id}`}>
                      <ArrowUpRight className="w-3 h-3 mr-1" /> Añadir
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* RULES TAB */}
      {tab === "rules" && (
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-5">
          <h3 className="text-slate-200 font-semibold text-sm mb-4">Creditos por accion IA</h3>
          <div className="space-y-2">
            {rules.map((r) => (
              <div key={r.action} className="flex items-center gap-3 py-2 border-b border-slate-800 last:border-0" data-testid={`rule-row-${r.action}`}>
                <Zap className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                <span className="text-xs text-slate-300 flex-1">{ACTION_LABELS[r.action] || r.action}</span>
                {editingRule === r.action ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      defaultValue={r.credits}
                      id={`rule-input-${r.action}`}
                      className="w-16 h-7 bg-slate-800 border-slate-600 text-white text-xs"
                    />
                    <button onClick={() => {
                      const val = document.getElementById(`rule-input-${r.action}`).value;
                      saveRule(r.action, val);
                    }} className="p-1 rounded text-emerald-400 hover:bg-slate-800"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditingRule(null)} className="p-1 rounded text-muted-foreground hover:bg-slate-800"><XIcon className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-violet-400">{r.credits}</span>
                    <button onClick={() => setEditingRule(r.action)} className="p-1 rounded text-muted-foreground hover:text-white hover:bg-slate-800">
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REQUESTS TAB */}
      {tab === "requests" && (
        <div className="space-y-3">
          {topUpRequests.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-10">No hay solicitudes pendientes</p>
          ) : (
            topUpRequests.map((r) => (
              <div key={r.request_id} className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex items-center gap-4" data-testid={`request-${r.request_id}`}>
                <div className="flex-1">
                  <span className="text-white text-sm font-medium">{r.restaurant_name}</span>
                  <span className="text-xs text-muted-foreground ml-2">{r.credits} creditos / {r.price} EUR</span>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(r.created_at).toLocaleDateString("es-ES")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-xs h-8"
                    onClick={() => handleTopUpRequest(r.request_id, "approve")} data-testid={`approve-${r.request_id}`}>
                    <Check className="w-3 h-3 mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 text-xs h-8"
                    onClick={() => handleTopUpRequest(r.request_id, "reject")}>
                    <XIcon className="w-3 h-3 mr-1" /> Rechazar
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* UPGRADES TAB */}
      {tab === "upgrades" && (
        <div className="space-y-3" data-testid="upgrades-tab">
          {upgradeRequests.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-10">No hay solicitudes de upgrade pendientes</p>
          ) : (
            upgradeRequests.map((r) => (
              <div key={r.request_id} className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex items-center gap-4" data-testid={`upgrade-${r.request_id}`}>
                <div className="w-9 h-9 bg-violet-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ArrowUpRight className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium">{r.restaurant_name}</span>
                    <span className="text-xs text-muted-foreground">({r.user_email})</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">{r.current_plan_name}</Badge>
                    <span className="text-muted-foreground text-xs">&rarr;</span>
                    <Badge className="bg-violet-500/15 text-violet-400 border-violet-500/30 text-[10px]">{r.target_plan_name}</Badge>
                    <span className="text-xs text-muted-foreground">{r.target_plan_price}&euro;/mes · {r.target_plan_credits} creditos</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-xs h-8"
                    onClick={() => handleUpgradeRequest(r.request_id, "approve")} data-testid={`approve-upgrade-${r.request_id}`}>
                    <Check className="w-3 h-3 mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 text-xs h-8"
                    onClick={() => handleUpgradeRequest(r.request_id, "reject")} data-testid={`reject-upgrade-${r.request_id}`}>
                    <XIcon className="w-3 h-3 mr-1" /> Rechazar
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* PRICING CONFIG TAB */}
      {tab === "pricing" && (
        <div className="space-y-6">
          {/* Annual Discount Config */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-400" />
              Descuento por pago anual
            </h3>
            {pricingConfig && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  {[
                    { value: "none", label: "Sin descuento" },
                    { value: "percent", label: "% Descuento" },
                    { value: "free_months", label: "Meses gratis" },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setPricingConfig(prev => ({ ...prev, annual_discount_type: opt.value }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        pricingConfig.annual_discount_type === opt.value ? "bg-violet-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {pricingConfig.annual_discount_type === "percent" && (
                  <div className="flex items-center gap-3">
                    <Label className="text-slate-400 text-xs">Porcentaje:</Label>
                    <Input type="number" min={1} max={50} value={pricingConfig.annual_discount_percent || 0}
                      onChange={e => setPricingConfig(prev => ({ ...prev, annual_discount_percent: Number(e.target.value) }))}
                      className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-sm" />
                    <span className="text-muted-foreground text-xs">% (max 50%)</span>
                  </div>
                )}
                {pricingConfig.annual_discount_type === "free_months" && (
                  <div className="flex items-center gap-3">
                    <Label className="text-slate-400 text-xs">Meses gratis:</Label>
                    <Input type="number" min={1} max={4} value={pricingConfig.annual_free_months || 0}
                      onChange={e => setPricingConfig(prev => ({ ...prev, annual_free_months: Number(e.target.value) }))}
                      className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-sm" />
                    <span className="text-muted-foreground text-xs">meses (paga {12 - (pricingConfig.annual_free_months || 0)}, lleva 12)</span>
                  </div>
                )}
                <Button size="sm" className="bg-violet-600 hover:bg-violet-500 text-xs" data-testid="save-pricing-config"
                  onClick={async () => {
                    try {
                      await adminFetch("/api/admin/pricing-config", { method: "PUT", body: JSON.stringify(pricingConfig) });
                      toast.success("Configuracion de precios guardada");
                      loadData();
                    } catch { toast.error("Error guardando"); }
                  }}>
                  <Save className="w-3 h-3 mr-1" /> Guardar configuracion
                </Button>
              </div>
            )}
          </div>

          {/* Seasonal Offers */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" />
                Ofertas temporales
              </h3>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-500 text-xs h-7" data-testid="add-offer-btn"
                onClick={() => setEditingOffer({ name: "", discount_percent: 10, applies_to: "both", start_date: new Date().toISOString().slice(0, 16), end_date: "", _isNew: true })}>
                <Plus className="w-3 h-3 mr-1" /> Nueva oferta
              </Button>
            </div>

            {/* Offer editor */}
            {editingOffer && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-[10px]">Nombre (ej: Black Friday)</Label>
                    <Input value={editingOffer.name || ""} onChange={e => setEditingOffer(prev => ({ ...prev, name: e.target.value }))}
                      className="h-8 bg-slate-800 border-slate-700 text-white text-sm" placeholder="Black Friday 2026" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-[10px]">Descuento %</Label>
                    <Input type="number" min={1} max={80} value={editingOffer.discount_percent || 10}
                      onChange={e => setEditingOffer(prev => ({ ...prev, discount_percent: Number(e.target.value) }))}
                      className="h-8 bg-slate-800 border-slate-700 text-white text-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400 text-[10px]">Aplica a:</Label>
                  <div className="flex gap-2 mt-1">
                    {[
                      { value: "monthly", label: "Solo mensual" },
                      { value: "annual", label: "Solo anual" },
                      { value: "both", label: "Ambos" },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setEditingOffer(prev => ({ ...prev, applies_to: opt.value }))}
                        className={`px-2.5 py-1 rounded text-[10px] font-medium ${editingOffer.applies_to === opt.value ? "bg-violet-600 text-white" : "bg-slate-800 text-slate-400"}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-[10px]">Fecha inicio</Label>
                    <Input type="datetime-local" value={(editingOffer.start_date || "").slice(0, 16)}
                      onChange={e => setEditingOffer(prev => ({ ...prev, start_date: e.target.value + ":00+00:00" }))}
                      className="h-8 bg-slate-800 border-slate-700 text-white text-sm" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-[10px]">Fecha fin</Label>
                    <Input type="datetime-local" value={(editingOffer.end_date || "").slice(0, 16)}
                      onChange={e => setEditingOffer(prev => ({ ...prev, end_date: e.target.value + ":00+00:00" }))}
                      className="h-8 bg-slate-800 border-slate-700 text-white text-sm" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-xs" data-testid="save-offer-btn"
                    onClick={async () => {
                      try {
                        if (editingOffer._isNew) {
                          await adminFetch("/api/admin/offers", { method: "POST", body: JSON.stringify(editingOffer) });
                        } else {
                          await adminFetch(`/api/admin/offers/${editingOffer.offer_id}`, { method: "PUT", body: JSON.stringify(editingOffer) });
                        }
                        toast.success("Oferta guardada");
                        setEditingOffer(null);
                        loadData();
                      } catch { toast.error("Error guardando oferta"); }
                    }}>
                    <Save className="w-3 h-3 mr-1" /> Guardar
                  </Button>
                  <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 text-xs"
                    onClick={() => setEditingOffer(null)}>Cancelar</Button>
                </div>
              </div>
            )}

            {/* Offers list */}
            {offers.length === 0 && !editingOffer ? (
              <p className="text-muted-foreground text-xs">No hay ofertas creadas</p>
            ) : (
              <div className="space-y-2">
                {offers.map(o => {
                  const now = new Date().toISOString();
                  const isActive = o.is_active && o.start_date <= now && o.end_date >= now;
                  return (
                    <div key={o.offer_id} className="flex items-center gap-3 bg-slate-800/30 rounded-lg p-3" data-testid={`offer-${o.offer_id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{o.name}</span>
                          <Badge className={isActive ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-700 text-slate-400"}>
                            {isActive ? "Activa" : o.is_active ? "Programada" : "Inactiva"}
                          </Badge>
                          <span className="text-[10px] text-violet-400 font-semibold">-{o.discount_percent}%</span>
                          <span className="text-[10px] text-muted-foreground">
                            {o.applies_to === "both" ? "mensual + anual" : o.applies_to === "monthly" ? "solo mensual" : "solo anual"}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(o.start_date).toLocaleDateString("es-ES")} — {new Date(o.end_date).toLocaleDateString("es-ES")}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                          onClick={() => setEditingOffer(o)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                          onClick={async () => {
                            if (!confirm("Eliminar oferta?")) return;
                            try {
                              await adminFetch(`/api/admin/offers/${o.offer_id}`, { method: "DELETE" });
                              toast.success("Oferta eliminada");
                              loadData();
                            } catch { toast.error("Error"); }
                          }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
